// src/components/cards/shared/dialogs/EmailQuoteDialog.tsx

import React, { useEffect, useState } from 'react';
import { Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import DialogBase from '../../../common/modal/DialogBase';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import LoadingSpinner from '../../../common/LoadingSpinner';

interface EmailQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (includeBookingForm: boolean) => void;
  clientName: string;
  clientEmail: string;
  sending: boolean;
}

const EmailQuoteDialog: React.FC<EmailQuoteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  clientName,
  clientEmail,
  sending,
}) => {
  const [includeBookingForm, setIncludeBookingForm] = useState(true);

  // Reset to default whenever the dialog is reopened.
  useEffect(() => {
    if (open) setIncludeBookingForm(true);
  }, [open]);

  return (
    <DialogBase
      draggable
      open={open}
      onClose={onClose}
      title="Send Quotation"
      headerIcon={<EmailIcon />}
      headerColor="primary"
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            disabled={sending}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={() => onConfirm(includeBookingForm)}
            disabled={sending}
            text={sending ? 'Sending...' : 'Send'}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
              '&.Mui-disabled': {
                backgroundColor: 'primary.main',
                color: 'white',
                opacity: 0.8,
              },
            }}
          />
        </>
      }
    >
      <Box sx={{ mt: 1, textAlign: 'center', position: 'relative' }}>
        {sending && (
          <Box
            sx={{
              position: 'absolute',
              inset: '-5px -24px -20px -24px',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(0,0,0,0.55)'
                  : 'rgba(255,255,255,0.75)',
              borderRadius: '4px',
            }}
          >
            <LoadingSpinner compact size={80} message="" />
          </Box>
        )}

        <Typography sx={{ fontSize: '1.8rem' }}>
          Send quotation to <strong>{clientName}</strong>
        </Typography>
        {clientEmail && (
          <Box
            sx={{
              overflowX: 'auto',
              touchAction: 'pan-x',
              mt: 0.5,
              px: 1,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Typography
              sx={{
                fontSize: '1.8rem',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {clientEmail}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeBookingForm}
                onChange={(e) => setIncludeBookingForm(e.target.checked)}
                disabled={sending}
                sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '1.6rem' }}>
                Include booking form
              </Typography>
            }
          />
        </Box>
      </Box>
    </DialogBase>
  );
};

export default EmailQuoteDialog;
